import React, { Component } from "react";
import API from "../utils/api";
import { toast } from "react-toastify";
import Select from "react-select";
import { connect } from "react-redux";
import { PERMISSION, ROLE, ROLES } from "../utils/constants";
import { hasPermission } from "../utils/permissions";

class TeamDashboardPage extends Component {
  state = {
    teams: [],
    selectedTeam: null,
    fromDate: "",
    toDate: "",
    stats: null,
    allTeamsStats: null,
    expandedTeams: {},
    loading: false,
    initialLoad: true,
  };

  componentDidMount() {
    const date = new Date();
    const firstDay = new Date(date.getFullYear(), date.getMonth(), date.getDate()+1).toISOString().split('T')[0];
    const lastDay = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1).toISOString().split('T')[0];
    this.setState({ fromDate: firstDay, toDate: lastDay }, () => {
      this.fetchTeams();
    });
  }

  fetchTeams = async () => {
    try {
      const res = await API.get("/teams");
      let teams = res.data.data || [];
      this.setState({ teams, initialLoad: false });
      if (teams.length > 0) {
        this.setState({ selectedTeam: { label: teams[0].name, value: teams[0].id } }, () => {
          this.fetchStats();
        });
      }
    } catch (e) {
      toast.error("Failed to load teams");
      this.setState({ initialLoad: false });
    }
  };

  fetchStats = async () => {
    const { selectedTeam } = this.state;
    if (!selectedTeam) return;

    if (selectedTeam.value === "ALL") {
      return this.fetchAllTeamsStats();
    }

    this.setState({ loading: true, allTeamsStats: null });
    try {
      const { fromDate, toDate } = this.state;
      const params = { team_id: selectedTeam.value };
      if (fromDate && toDate) {
        params.from_date = fromDate;
        params.to_date = toDate;
      }
      const res = await API.get("/team/dashboard-stats", { params });
      this.setState({ stats: res.data.data, loading: false });
    } catch (e) {
      toast.error("Failed to load dashboard stats");
      this.setState({ loading: false });
    }
  };

  fetchAllTeamsStats = async () => {
    const { fromDate, toDate } = this.state;
    this.setState({ loading: true, stats: null });
    try {
      const params = {};
      if (fromDate && toDate) {
        params.from_date = fromDate;
        params.to_date = toDate;
      }
      const res = await API.get("/teams/dashboard-stats/all", { params });
      const allTeamsStats = res.data.data || [];
      const expandedTeams = {};
      allTeamsStats.forEach((t, idx) => { expandedTeams[t.team_id || idx] = idx === 0; });
      this.setState({ allTeamsStats, expandedTeams, loading: false });
    } catch (e) {
      toast.error("Failed to load dashboard stats for all teams");
      this.setState({ loading: false });
    }
  };

  toggleTeam = (teamKey) => {
    this.setState(prev => ({
      expandedTeams: { ...prev.expandedTeams, [teamKey]: !prev.expandedTeams[teamKey] },
    }));
  };

  formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  };

  buildCSVForTeam = (teamStats, fromDate, toDate) => {
    const rows = [];
    const dates = teamStats.dates || [];
    const users = teamStats.users || [];
    const plans = teamStats.active_plans || [];

    // Team header
    rows.push([`"Team: ${teamStats.team_name}"`]);

    // Plans info
    if (plans.length > 0) {
      plans.forEach(plan => {
        const slabsText = plan.slabs && plan.slabs.length > 0
          ? plan.slabs.map(s => `${s.metric_type} ${s.min_count}-${s.max_count || '+'}: ${s.points_per_lead} pts`).join(' | ')
          : 'None';
        rows.push([`"Plan: ${plan.name} (${plan.plan_type}) | ${plan.effective_from || 'Start'} to ${plan.effective_to || 'Ongoing'} | Slabs: ${slabsText}"`]);
      });
    } else {
      rows.push([`"No Incentive Plan Applied"`]);
    }
    rows.push([]);

    // Table headers
    const headerRow1 = ["Name", "Role", "Metric"];
    dates.forEach(d => headerRow1.push(this.formatDate(d)));
    headerRow1.push("Total");
    rows.push(headerRow1);

    // User rows: each user gets 3 sub-rows
    users.forEach(u => {
      const daily = u.daily || {};
      const role = u.is_coordinator ? ROLE.TEAM_COORDINATOR : ROLE.EXECUTIVE;

      // Posted row
      const postedRow = [`"${u.name}"`, role, "Posted"];
      let totalPosted = 0;
      dates.forEach(d => {
        const val = daily[d]?.posted || 0;
        postedRow.push(val);
        totalPosted += val;
      });
      postedRow.push(totalPosted);
      rows.push(postedRow);

      // Attempted row
      const attemptedRow = ["", "", "Attempted"];
      let totalAttempted = 0;
      dates.forEach(d => {
        const val = daily[d]?.attempted || 0;
        attemptedRow.push(val);
        totalAttempted += val;
      });
      attemptedRow.push(totalAttempted);
      rows.push(attemptedRow);

      // Incentive row
      const incentiveRow = ["", "", "Incentive"];
      dates.forEach(d => {
        const val = daily[d]?.incentive || 0;
        incentiveRow.push(val);
      });
      incentiveRow.push(u.points || 0);
      rows.push(incentiveRow);
    });

    // Team totals
    const totalPostedRow = [`"${teamStats.team_name} Total"`, "", "Posted"];
    const totalAttemptedRow = ["", "", "Attempted"];
    const totalIncentiveRow = ["", "", "Incentive"];
    let grandPosted = 0, grandAttempted = 0, grandIncentive = 0;

    dates.forEach(d => {
      let dPosted = 0, dAttempted = 0, dIncentive = 0;
      users.forEach(u => {
        const daily = u.daily || {};
        dPosted += daily[d]?.posted || 0;
        dAttempted += daily[d]?.attempted || 0;
        dIncentive += daily[d]?.incentive || 0;
      });
      totalPostedRow.push(dPosted);
      totalAttemptedRow.push(dAttempted);
      totalIncentiveRow.push(Math.round(dIncentive * 100) / 100);
      grandPosted += dPosted;
      grandAttempted += dAttempted;
      grandIncentive += dIncentive;
    });
    totalPostedRow.push(grandPosted);
    totalAttemptedRow.push(grandAttempted);
    totalIncentiveRow.push(Math.round(grandIncentive * 100) / 100);
    rows.push(totalPostedRow);
    rows.push(totalAttemptedRow);
    rows.push(totalIncentiveRow);

    return rows;
  };

  handleDownloadCSV = async () => {
    const { fromDate, toDate } = this.state;
    const toastId = toast.info("Generating CSV for all teams...", { autoClose: false });

    try {
      const params = {};
      if (fromDate && toDate) {
        params.from_date = fromDate;
        params.to_date = toDate;
      }

      const res = await API.get("/teams/dashboard-stats/all", { params });
      const allTeamsStats = res.data.data;

      const rows = [];
      rows.push([`"Date Filter Applied:"`, `"${fromDate || 'Start'} to ${toDate || 'End'}"`]);
      rows.push([]);

      allTeamsStats.forEach(teamStats => {
        const teamRows = this.buildCSVForTeam(teamStats, fromDate, toDate);
        teamRows.forEach(r => rows.push(r));
        rows.push([]);
        rows.push([]);
      });

      if (rows.length <= 3) {
        toast.dismiss(toastId);
        toast.info("No data available to download");
        return;
      }

      const csvContent = "data:text/csv;charset=utf-8,"
        + rows.map(e => e.join(",")).join("\n");

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      const dateStr = `${fromDate || 'all'}_to_${toDate || 'all'}`;
      link.setAttribute("download", `All_Teams_Incentives_${dateStr}.csv`.replace(/\s+/g, '_'));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.dismiss(toastId);
      toast.success("CSV Downloaded successfully");
    } catch (e) {
      toast.dismiss(toastId);
      toast.error("Failed to generate CSV");
    }
  };

  renderTeamCard = (stats) => {
    const dates = stats?.dates || [];
    const users = stats?.users || [];
    const activePlans = stats?.active_plans || [];

    return (
      <>
        {/* Incentive Plans */}
        {activePlans.length > 0 && activePlans.map((plan, idx) => (
          <div className="row mt-2" key={plan.id || idx}>
            <div className="col-md-12">
              <div className="card">
                <div className="card-header bg-white">
                  <h5 className="mb-0">
                    Incentive Plan {idx + 1}: {plan.name} ({plan.plan_type})
                    <small className="text-muted ms-2">
                      | {plan.effective_from || 'Start'} to {plan.effective_to || 'Ongoing'}
                    </small>
                  </h5>
                </div>
                <div className="card-body p-0">
                  <table className="table table-bordered mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>Metric Type</th>
                        <th>Min Count</th>
                        <th>Max Count</th>
                        <th>Points Per Lead</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.slabs?.map(s => (
                        <tr key={s.id}>
                          <td className="text-capitalize">{s.metric_type}</td>
                          <td>{s.min_count}</td>
                          <td>{s.max_count || 'Above'}</td>
                          <td>{s.points_per_lead}</td>
                        </tr>
                      ))}
                      {(!plan.slabs || plan.slabs.length === 0) && (
                        <tr><td colSpan="4" className="text-center">No slabs found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        ))}

        {activePlans.length === 0 && (
          <div className="alert alert-warning mt-2">No incentive plan applied for this period.</div>
        )}

        {/* Summary Cards */}
        <div className="row mt-3">
          <div className="col-md-4">
            <div className="card text-white bg-primary mb-3">
              <div className="card-body text-center">
                <h1 className="display-4">{stats.attempted_count}</h1>
                <p className="card-text">Total Attempted Leads</p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card text-white bg-success mb-3">
              <div className="card-body text-center">
                <h1 className="display-4">{stats.posted_count}</h1>
                <p className="card-text">Total Posted Leads</p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card text-white bg-warning mb-3">
              <div className="card-body text-center">
                <h1 className="display-4">{stats.points}</h1>
                <p className="card-text">Incentive Points Earned</p>
              </div>
            </div>
          </div>
        </div>

        {/* Daily Breakdown Table */}
        <div className="row mt-4">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header bg-white">
                <h5 className="mb-0">Daily Breakdown</h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-bordered table-striped mb-0" style={{ fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th rowSpan="2" style={{ verticalAlign: "middle" }}>Name</th>
                        <th rowSpan="2" style={{ verticalAlign: "middle" }}>Metric</th>
                        {dates.map(d => (
                          <th key={d} className="text-center" style={{ minWidth: 60 }}>
                            {this.formatDate(d)}
                          </th>
                        ))}
                        <th className="text-center" style={{ fontWeight: "bold", minWidth: 70 }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr><td colSpan={dates.length + 4} className="text-center">No members found</td></tr>
                      ) : (
                        <>
                          {users.map(u => {
                            const daily = u.daily || {};
                            let totalPosted = 0, totalAttempted = 0;
                            dates.forEach(d => {
                              totalPosted += daily[d]?.posted || 0;
                              totalAttempted += daily[d]?.attempted || 0;
                            });

                            return (
                              <React.Fragment key={u.user_id}>
                                {/* Posted row */}
                                <tr>
                                  <td rowSpan="3" style={{ verticalAlign: "middle", fontWeight: 500 }}>{u.name}  {u.is_coordinator
                                    ? <span className="badge bg-info">Coordinator</span>
                                    : <span className="badge bg-secondary">Executive</span>}
                                  </td>
                                  <td><strong style={{ color: "#27ae60" }}>Posted</strong></td>
                                  {dates.map(d => (
                                    <td key={d} className="text-center">{daily[d]?.posted || 0}</td>
                                  ))}
                                  <td className="text-center" style={{ fontWeight: "bold" }}>{totalPosted}</td>
                                </tr>
                                {/* Attempted row */}
                                <tr>
                                  <td><strong style={{ color: "#2980b9" }}>Attempted</strong></td>
                                  {dates.map(d => (
                                    <td key={d} className="text-center">{daily[d]?.attempted || 0}</td>
                                  ))}
                                  <td className="text-center" style={{ fontWeight: "bold" }}>{totalAttempted}</td>
                                </tr>
                                {/* Incentive row */}
                                <tr>
                                  <td><strong style={{ color: "#e67e22" }}>Incentive</strong></td>
                                  {dates.map(d => {
                                    const dayData = daily[d] || {};
                                    return (
                                      <td key={d} className="text-center" style={{ position: "relative" }}>
                                        {dayData.flagged_quota_exceeded ? (
                                          <>
                                            <del className="text-muted" style={{ textDecorationStyle: 'double' }}>{dayData.original_incentive || 0}</del>
                                            {" "}
                                            <span className="text-danger">0</span>
                                          </>
                                        ) : (
                                          dayData.incentive || 0
                                        )}
                                        {dayData.flagged_quota_exceeded && (
                                          <div style={{ color: "red", fontSize: "10px", lineHeight: 1, marginTop: "2px" }}>
                                            Flagged
                                          </div>
                                        )}
                                      </td>
                                    );
                                  })}
                                  <td className="text-center" style={{ fontWeight: "bold", position: "relative" }}>
                                    {u.flagged_quota_exceeded ? (
                                      <>
                                        <del className="text-muted" style={{ textDecorationStyle: 'double' }}>{u.original_points || 0}</del>
                                        {" "}
                                        <span className="text-danger">{u.points || 0}</span>
                                        <div style={{ color: "red", fontSize: "10px", lineHeight: 1, marginTop: "2px", fontWeight: "normal" }}>
                                          Flagged
                                        </div>
                                      </>
                                    ) : (
                                      u.points || 0
                                    )}
                                  </td>
                                </tr>
                              </React.Fragment>
                            );
                          })}

                          {/* Team Totals */}
                          <tr style={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}>
                            <td rowSpan="3" style={{ verticalAlign: "middle" }}>Team Total</td>
                            <td style={{ color: "#27ae60" }}>Posted</td>
                            {dates.map(d => {
                              let v = 0;
                              users.forEach(u => { v += u.daily?.[d]?.posted || 0; });
                              return <td key={d} className="text-center">{v}</td>;
                            })}
                            <td className="text-center">{stats.posted_count}</td>
                          </tr>
                          <tr style={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}>
                            <td style={{ color: "#2980b9" }}>Attempted</td>
                            {dates.map(d => {
                              let v = 0;
                              users.forEach(u => { v += u.daily?.[d]?.attempted || 0; });
                              return <td key={d} className="text-center">{v}</td>;
                            })}
                            <td className="text-center">{stats.attempted_count}</td>
                          </tr>
                          <tr style={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}>
                            <td style={{ color: "#e67e22" }}>Incentive</td>
                            {dates.map(d => {
                              let v = 0;
                              users.forEach(u => { v += u.daily?.[d]?.incentive || 0; });
                              return <td key={d} className="text-center">{Math.round(v * 100) / 100}</td>;
                            })}
                            <td className="text-center">{stats.points}</td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  render() {
    const { teams, selectedTeam, fromDate, toDate, stats, allTeamsStats, expandedTeams, loading, initialLoad } = this.state;
    const { user } = this.props;

    if (initialLoad) return <div className="p-4">Loading...</div>;

    const canViewAllTeams = hasPermission(user.permissions, PERMISSION.TEAM_DASHBOARD_VIEW_ALL_TEAMS);
    const teamOptions = teams.map(t => ({ label: t.name, value: t.id }));
    if (canViewAllTeams) {
      teamOptions.unshift({ label: "All Teams", value: "ALL" });
    }
    const showDownloadBtn = hasPermission(user.permissions, PERMISSION.TEAM_DASHBOARD_DOWNLOAD_CSV);
    const isAllTeamsView = selectedTeam?.value === "ALL";

    const grandTotals = isAllTeamsView && allTeamsStats ? allTeamsStats.reduce((acc, t) => {
      acc.attempted_count += t.attempted_count || 0;
      acc.posted_count += t.posted_count || 0;
      acc.points += t.points || 0;
      return acc;
    }, { attempted_count: 0, posted_count: 0, points: 0 }) : null;

    return (
      <div className="container-fluid p-4" style={{ backgroundColor: "#ecf0f5", minHeight: "100vh" }}>
        <h3 className="mb-4">Team Dashboard</h3>

        <div className="card mb-4">
          <div className="card-body">
            <div className="row">
              <div className="col-md-3">
                <label>Select Team</label>
                <Select
                  options={teamOptions}
                  value={selectedTeam}
                  onChange={val => this.setState({ selectedTeam: val }, this.fetchStats)}
                  placeholder="Select a team..."
                />
              </div>
              <div className="col-md-3">
                <label>From Date</label>
                <input type="date" className="form-control" value={fromDate} onChange={e => this.setState({ fromDate: e.target.value }, this.fetchStats)} />
              </div>
              <div className="col-md-3">
                <label>To Date</label>
                <input type="date" className="form-control" value={toDate} onChange={e => this.setState({ toDate: e.target.value }, this.fetchStats)} />
              </div>
              <div className="col-md-3">
                <label>&nbsp;</label><br />
                <button className="btn btn-info w-100" onClick={this.fetchStats}>
                  <i className="fas fa-filter"></i> Filter
                </button>
                <label>&nbsp;&nbsp;</label>
                {showDownloadBtn && (
                  <button className="btn btn-success btn-sm" onClick={this.handleDownloadCSV}>
                    <i className="fa fa-download me-1"></i> Download CSV
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center p-5">Loading stats...</div>
        ) : isAllTeamsView && allTeamsStats ? (
          <>
            <div className="row mt-3">
              <div className="col-md-12">
                <h4 className="mb-3">All Teams — Combined Totals</h4>
              </div>
              <div className="col-md-4">
                <div className="card text-white bg-primary mb-3">
                  <div className="card-body text-center">
                    <h1 className="display-4">{grandTotals.attempted_count}</h1>
                    <p className="card-text">Total Attempted Leads</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card text-white bg-success mb-3">
                  <div className="card-body text-center">
                    <h1 className="display-4">{grandTotals.posted_count}</h1>
                    <p className="card-text">Total Posted Leads</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card text-white bg-warning mb-3">
                  <div className="card-body text-center">
                    <h1 className="display-4">{Math.round(grandTotals.points * 100) / 100}</h1>
                    <p className="card-text">Incentive Points Earned</p>
                  </div>
                </div>
              </div>
            </div>

            <hr className="my-4" />
            <h4 className="mb-3">Team-wise Breakdown</h4>

            {allTeamsStats.length === 0 ? (
              <div className="alert alert-info">No teams found.</div>
            ) : (
              <div className="accordion">
                {allTeamsStats.map((teamStats, idx) => {
                  const teamKey = teamStats.team_id || idx;
                  const isOpen = !!expandedTeams[teamKey];
                  return (
                    <div key={teamKey} className="card mb-3">
                      <div
                        className="card-header d-flex justify-content-between align-items-center"
                        style={{ cursor: "pointer", backgroundColor: "#e9ecef" }}
                        onClick={() => this.toggleTeam(teamKey)}
                      >
                        <div className="d-flex align-items-center">

                          <h4 className="mb-0">{teamStats.team_name}</h4>
                          <span className="badge bg-primary me-2">Attempted: {teamStats.attempted_count}</span>&nbsp;
                          <span className="badge bg-success me-2">Posted: {teamStats.posted_count}</span>&nbsp;
                          <span className="badge bg-warning text-dark me-3">Points: {teamStats.points}</span> &nbsp;
                          <i className={`fas fa-chevron-${isOpen ? "up" : "down"}`}></i>
                          <br/>
                        </div>
                      </div>
                      {isOpen && (
                        <div className="card-body bg-light">
                          <br/>
                          {this.renderTeamCard(teamStats)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : stats ? (
          this.renderTeamCard(stats)
        ) : (
          <div className="alert alert-info">Please select a team to view stats.</div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state,
});

export default connect(mapStateToProps)(TeamDashboardPage);
